import React, { useState, useRef, useContext, useEffect } from 'react';
import { Box, Modal, Typography } from '@material-ui/core';
import { doc, setDoc, getDoc, collection, addDoc, query, where, getDocs } from "firebase/firestore";
import { firestore } from '../../firebase';
import { connect } from 'react-redux';
import { isNativeApp } from '../../util/platformDetection';

const modalBoxStyle = {
  position: 'absolute',
  top: 'calc(10% - 32.5px)',
  left: 'calc(50% - 200px)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

const BookmarkModal = ({ onClose, onSave, gameId, user, isViewGameContext = false }) => {


  const [modalOpen, setModalOpen] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [showConfirmPublicModal, setShowConfirmPublicModal] = useState(false);
  const [folderToMakePublic, setFolderToMakePublic] = useState(null);
  const [expandedFolder, setExpandedFolder] = useState(null);
  const [draggingPGN, setDraggingPGN] = useState(null);
  const [hasAddedNewFolder, setHasAddedNewFolder] = useState(false);
  const [activeTab, setActiveTab] = useState('public'); // Default to public tab
  const [newFolderPrivate, setNewFolderPrivate] = useState(false);
  const [notification, setNotification] = useState(null);

  // Always include the default folders
  const [folders, setFolders] = useState([
    { name: 'Featured', isPrivate: false, pgns: [] },
    { name: 'Safe', isPrivate: true, pgns: [] },
  ]);

  const folderRefs = useRef({});

  // Show notification function
  const showNotification = (message, type = 'success') => {
    setNotification({
      message,
      type
    });

    // Auto-hide notification after 3 seconds
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  // Add useEffect to fetch user's folders when component mounts
  useEffect(() => {
    const fetchUserFolders = async () => {
      if (!user || !user.credentials || !user.credentials.userId) {
        // console.error('User not logged in or user ID not available');
        return;
      }

      try {
        const userId = user.credentials.userId;

        // Fetch bookmark folders for the current user
        const folderQuery = query(
          collection(firestore, "bookmarkFolders"),
          where("userId", "==", userId)
        );

        const folderSnapshot = await getDocs(folderQuery);

        if (!folderSnapshot.empty) {
          // Process folder documents
          const fetchedFolders = folderSnapshot.docs.map(doc => ({
            id: doc.id,
            name: doc.data().folderName,
            isPrivate: doc.data().isPrivate,
            pgns: doc.data().games || [],
            folderIndex: doc.data().folderIndex !== undefined ? doc.data().folderIndex : 999
          }));

          // Merge with default folders, ensuring defaults are always present
          let mergedFolders = [...fetchedFolders];

          // Check if Featured folder exists, if not keep the default
          if (!fetchedFolders.some(folder => folder.name === 'Featured')) {
            mergedFolders.push({
              name: 'Featured',
              isPrivate: false,
              pgns: [],
              folderIndex: 0 // Featured is always index 0 in public folders
            });
          } else {
            // Ensure Featured has index 0
            mergedFolders = mergedFolders.map(folder =>
              folder.name === 'Featured' ? { ...folder, folderIndex: 0 } : folder
            );
          }

          // Check if Safe folder exists, if not keep the default
          if (!fetchedFolders.some(folder => folder.name === 'Safe')) {
            mergedFolders.push({
              name: 'Safe',
              isPrivate: true,
              pgns: [],
              folderIndex: 0 // Safe is always index 0 in private folders
            });
          } else {
            // Ensure Safe has index 0
            mergedFolders = mergedFolders.map(folder =>
              folder.name === 'Safe' ? { ...folder, folderIndex: 0 } : folder
            );
          }

          // Sort folders by folderIndex within their privacy category
          mergedFolders.sort((a, b) => {
            // First sort by privacy
            if (a.isPrivate !== b.isPrivate) {
              return a.isPrivate ? 1 : -1;
            }
            // Then sort by folderIndex
            return a.folderIndex - b.folderIndex;
          });

          setFolders(mergedFolders);
        }
      } catch (error) {
        console.error("Error fetching user folders:", error);
      }
    };

    fetchUserFolders();
  }, [user]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    // When switching tabs, also update the privacy setting for new folders
    setNewFolderPrivate(tab === 'private');
  };

  const toggleFolderExpand = (folderName) => {
    if (expandedFolder === folderName) {
      setExpandedFolder(null);
    } else {
      setExpandedFolder(folderName);
    }
  };

  const handleFolderClick = (folderName) => {
    setSelectedFolder(folderName === selectedFolder ? '' : folderName);
    setShowNewFolderInput(false);
    toggleFolderExpand(folderName);
  };

  const handleSaveToNewFolder = async () => {


    if (!newFolderName.trim()) {
      showNotification('Please enter a folder name', 'error');
      return;
    }

    try {
      // Check if user is logged in
      if (!user || !user.credentials || !user.credentials.userId) {
        // console.error('User not logged in or user ID not available');
        showNotification('You must be logged in to bookmark games', 'error');
        return;
      }

      const userId = user.credentials.userId;

      // Check if folder with this name already exists
      const folderQuery = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", userId),
        where("folderName", "==", newFolderName.trim())
      );

      const folderSnapshot = await getDocs(folderQuery);

      if (!folderSnapshot.empty) {
        showNotification(`Folder "${newFolderName.trim()}" already exists`, 'error');
        return;
      }

      // Get the highest folderIndex in the appropriate category
      const isPrivate = newFolderPrivate;
      const categoryFolders = folders.filter(f =>
        f.isPrivate === isPrivate &&
        f.name !== 'Featured' &&
        f.name !== 'Safe'
      );

      const maxIndex = categoryFolders.length > 0
        ? Math.max(...categoryFolders.map(f => f.folderIndex), 0)
        : 0;

      // Create new folder with the game at index 0
      const newFolderData = {
        userId: userId,
        folderName: newFolderName.trim(),
        isPrivate: newFolderPrivate,
        createdAt: new Date().toISOString(),
        folderIndex: maxIndex + 1, // Add to the end of the appropriate category
        games: [{
          gameId: gameId,
          index: 0,
          createdAt: new Date().toISOString()
        }]
      };



      // Add a new document to the "bookmarkFolders" collection
      const docRef = await addDoc(collection(firestore, "bookmarkFolders"), newFolderData);


      // Highlight the bookmark button in sidebar
      highlightBookmarkButton();

      // Call the onSave callback with folder name and privacy setting
      onSave(newFolderName.trim(), newFolderPrivate);

      // Close the modal immediately
      setModalOpen(false);
      onClose && onClose();
    } catch (error) {
      console.error("Error creating new folder:", error);
      showNotification('Failed to create folder. Please try again.', 'error');
    }
  };

  const handleSaveToFolder = async (folderName) => {


    try {
      // Check if user is logged in
      if (!user || !user.credentials || !user.credentials.userId) {
        // console.error('User not logged in or user ID not available');
        showNotification('You must be logged in to bookmark games', 'error');
        return;
      }

      const userId = user.credentials.userId;

      // Find the folder to get its privacy setting
      const folder = folders.find(f => f.name === folderName);
      if (!folder) {
        console.error(`Folder "${folderName}" not found`);
        return;
      }


      // Check if the folder document already exists in Firestore
      const folderQuery = query(
        collection(firestore, "bookmarkFolders"),
        where("userId", "==", userId),
        where("folderName", "==", folderName)
      );

      const folderSnapshot = await getDocs(folderQuery);

      if (folderSnapshot.empty) {
        // Folder doesn't exist yet, create a new one with the game at index 0
        const newFolderData = {
          userId: userId,
          folderName: folderName,
          isPrivate: folder.isPrivate,
          createdAt: new Date().toISOString(),
          games: [{
            gameId: gameId,
            index: 0,
            createdAt: new Date().toISOString()
          }]
        };



        // Add a new document to the "bookmarkFolders" collection
        const docRef = await addDoc(collection(firestore, "bookmarkFolders"), newFolderData);

      } else {
        // Folder exists, update it by adding the game at index 0 and reindexing others
        const folderDoc = folderSnapshot.docs[0];
        const folderData = folderDoc.data();

        // Check if the game is already in the folder
        const existingGameIndex = folderData.games ? folderData.games.findIndex(game => game.gameId === gameId) : -1;

        if (existingGameIndex !== -1) {

          // Game already exists, just update its index to 0 and reindex others
          const updatedGames = [...folderData.games];
          const existingGame = updatedGames.splice(existingGameIndex, 1)[0];

          // Increment index of all other games
          updatedGames.forEach(game => {
            game.index += 1;
          });

          // Add the existing game at index 0
          existingGame.index = 0;
          updatedGames.unshift(existingGame);

          // Update the folder document
          await setDoc(folderDoc.ref, {
            ...folderData,
            games: updatedGames,
            updatedAt: new Date().toISOString()
          });


        } else {
          // Game doesn't exist in the folder, add it at index 0
          const updatedGames = folderData.games || [];

          // Increment index of all existing games
          updatedGames.forEach(game => {
            game.index += 1;
          });

          // Add the new game at index 0
          updatedGames.unshift({
            gameId: gameId,
            index: 0,
            createdAt: new Date().toISOString()
          });

          // Update the folder document
          await setDoc(folderDoc.ref, {
            ...folderData,
            games: updatedGames,
            updatedAt: new Date().toISOString()
          });


        }
      }

      // Highlight the bookmark button in sidebar
      highlightBookmarkButton();

      // Call the onSave callback with folder name and privacy setting
      onSave(folderName, folder.isPrivate);

      // Close the modal immediately
      setModalOpen(false);
      onClose && onClose();
    } catch (error) {
      console.error("Error saving bookmark: ", error);
      showNotification('Failed to save bookmark. Please try again.', 'error');
    }
  };

  // Function to highlight the bookmark button in the sidebar
  const highlightBookmarkButton = () => {
    // Find the bookmark button in the sidebar by ID
    const bookmarkButton = document.getElementById('sidebar-bookmark-button');

    if (bookmarkButton) {
      // Store the original background color
      const originalBackground = bookmarkButton.style.backgroundColor;
      const originalColor = bookmarkButton.style.color;

      // Change to green

      bookmarkButton.style.color = '#58b047';
      bookmarkButton.style.transition = 'color 0.5s';

      // Reset after 2 seconds
      setTimeout(() => {
        bookmarkButton.style.backgroundColor = originalBackground;
        bookmarkButton.style.color = originalColor;
      }, 3000);
    }
  };

  // Filter folders based on active tab
  const filteredFolders = folders.filter(folder =>
    (activeTab === 'public' && !folder.isPrivate) ||
    (activeTab === 'private' && folder.isPrivate)
  );

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        zIndex: 1300,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '20px',
        paddingTop: 'calc(20vh + env(safe-area-inset-top))' // Moved down to avoid iPhone notch cutoff
      }}
      onClick={() => {
        setModalOpen(false);
        onClose && onClose();
      }}
    >
      <div 
        style={{
          width: '85%',
          maxWidth: '400px',
          backgroundColor: '#1a1a1a',
          border: '1px solid #444',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
          padding: '20px',
          color: 'white',
          outline: 'none',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Notification popup */}
        {notification && (
          <div style={{
            position: "fixed",
            left: "50%",
            top: isNativeApp() ? "calc(50% + 30px)" : "50%",
            transform: "translate(-50%, -50%)",
            backgroundColor: notification.type === 'error' ? 'rgba(255, 0, 0, 0.9)' : 'rgba(0, 128, 0, 0.9)',
            color: 'white',
            padding: '15px 30px',
            borderRadius: '8px',
            zIndex: 1400,
            fontSize: '16px',
            fontWeight: 'bold',
            textAlign: 'center',
            minWidth: '300px',
            maxWidth: '500px',
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {notification.message}
          </div>
        )}

        <h2 id="bookmark-modal-title" style={{
          textAlign: 'center',
          color: 'gold',
          marginTop: 0,
          marginBottom: '20px'
        }}>
{isViewGameContext ? 'Bookmark Game' : 'Save Game to Bookmark'}
        </h2>

        {/* Tab navigation for Public/Private folders */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          marginBottom: '20px'
        }}>
          <button
            style={{
              flex: 1,
              padding: '10px',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'public' ? '3px solid gold' : 'none',
              color: activeTab === 'public' ? 'gold' : 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
            onClick={() => handleTabClick('public')}
          >
{isViewGameContext ? 'Public' : 'Public Folders'}
          </button>
          <button
            style={{
              flex: 1,
              padding: '10px',
              backgroundColor: 'transparent',
              border: 'none',
              borderBottom: activeTab === 'private' ? '3px solid gold' : 'none',
              color: activeTab === 'private' ? 'gold' : 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer'
            }}
            onClick={() => handleTabClick('private')}
          >
{isViewGameContext ? 'Private' : 'Private Folders'}
          </button>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          width: '100%'
        }}>
          {/* Folder list */}
          <div style={{
            width: '100%',
            marginBottom: '20px',
            maxHeight: '300px',
            overflowY: 'auto'
          }}>
            {filteredFolders.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '20px',
                color: '#aaa',
                fontStyle: 'italic'
              }}>
                No {activeTab} folders found
              </div>
            ) : (
              filteredFolders.map((folder) => (
                <div
                  key={folder.name}
                  ref={el => folderRefs.current[folder.name] = el}
                  style={{
                    marginBottom: '10px',
                    backgroundColor: '#333',
                    borderRadius: '6px',
                    border: '1px solid #555',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      padding: '15px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      backgroundColor: selectedFolder === folder.name ? 'rgba(255,215,0,0.15)' : 'transparent',
                      borderLeft: selectedFolder === folder.name ? '3px solid gold' : 'none'
                    }}
                    onClick={() => handleFolderClick(folder.name)}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, marginRight: '10px' }}>
                      <i
                        className="fa-solid fa-folder"
                        style={{ marginRight: '10px', color: 'gold', fontSize: '20px', flexShrink: 0 }}
                      ></i>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {folder.name}
                      </span>
                      {folder.isPrivate && (
                        <i className="fa-solid fa-eye-slash" style={{ marginLeft: '10px', color: 'gray', fontSize: '14px', flexShrink: 0 }}></i>
                      )}
                      {(folder.name === 'Featured' || folder.name === 'Safe') && (
                        <span style={{
                          marginLeft: '10px',
                          fontSize: '12px',
                          color: '#aaa',
                          backgroundColor: 'rgba(0,0,0,0.3)',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          flexShrink: 0
                        }}>
                          Default
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSaveToFolder(folder.name);
                        }}
                        style={{
                          padding: '6px 12px',
                          borderRadius: '5px',
                          color: 'black',
                          backgroundColor: 'gold',
                          border: 'none',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: '12px'
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Create New Folder UI */}
          <div style={{
            marginBottom: '10px',
            width: '100%',
            maxWidth: '500px'
          }}>
            {showNewFolderInput ? (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                width: '100%'
              }}>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter folder name"
                  style={{
                    width: '100%',
                    backgroundColor: '#333',
                    border: '1px solid #666',
                    color: 'white',
                    borderRadius: '6px',
                    fontSize: '16px',
                    height: '40px',
                    paddingLeft: '12px',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                  autoFocus
                />

                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%'
                }}>
                  <div
                    onClick={() => {
                      // Only allow toggling if not a default folder
                      setNewFolderPrivate(!newFolderPrivate);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={newFolderPrivate}
                      onChange={() => { }}
                      style={{
                        cursor: 'pointer',
                        width: '18px',
                        height: '18px'
                      }}
                    />
                    <span style={{ marginLeft: '5px', fontSize: '14px', color: '#ddd' }}>
                      Private
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => setShowNewFolderInput(false)}
                      style={{
                        padding: '8px 15px',
                        borderRadius: '6px',
                        color: '#888',
                        fontSize: '14px',
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                        border: '1px solid #888'
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleSaveToNewFolder}
                      disabled={!newFolderName.trim()}
                      style={{
                        padding: '8px 15px',
                        borderRadius: '6px',
                        color: 'black',
                        fontSize: '14px',
                        cursor: newFolderName.trim() ? 'pointer' : 'not-allowed',
                        backgroundColor: newFolderName.trim() ? 'gold' : '#555',
                        border: 'none',
                        fontWeight: 'bold'
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setShowNewFolderInput(true);
                  setSelectedFolder('');
                }}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '5px',
                  color: 'gold',
                  fontSize: '16px',
                  cursor: 'pointer',
                  backgroundColor: 'transparent',
                  border: '2px solid gold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold'
                }}
              >
                <i className="fa-solid fa-plus" style={{ marginRight: '10px' }}></i>
                Create New Folder
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.user
});

export default connect(mapStateToProps)(BookmarkModal);
